import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { Bell, AlertCircle, Clock } from "lucide-react";
import { ICON_BTN } from "@/lib/styles";
import { toast } from "sonner";

export function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ overdue: [], upcoming: [], count: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const alarmedRef = useRef(new Set(JSON.parse(localStorage.getItem("notacora_alarmed") || "[]")));

  const playBeep = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "sine"; osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.6);
      osc.start(); osc.stop(ctx.currentTime + 0.6);
    } catch { /* silencioso */ }
  };

  const fireAlarms = useCallback((tasks) => {
    if (typeof Notification === "undefined") return;
    const now = Date.now();
    let changed = false;
    const notify = (title, body) => {
      if (Notification.permission === "granted") {
        try { new Notification("\u23f0 " + title, { body, icon: "/icon-192.png" }); } catch { /* noop */ }
      }
      playBeep();
      toast(`\u23f0 ${title}`, { description: body });
    };
    const label = (m) => (m >= 1440 ? "1 día" : m >= 60 ? `${Math.round(m / 60)} h` : `${m} min`);
    for (const t of tasks) {
      if (!t.due_date || t.due_date.length <= 10) continue; // solo citas con hora
      const due = new Date(t.due_date).getTime();
      const mins = Number(t.reminder_minutes) || 0;
      // recordatorio anticipado
      if (mins > 0) {
        const pre = due - mins * 60000;
        const preKey = `${t.id}:${t.due_date}:pre${mins}`;
        if (now >= pre && now - pre <= 180000 && !alarmedRef.current.has(preKey)) {
          alarmedRef.current.add(preKey); changed = true;
          notify(t.title, `Tu cita es en ${label(mins)}`);
        }
      }
      // hora exacta
      const dueKey = `${t.id}:${t.due_date}:due`;
      if (now >= due && now - due <= 180000 && !alarmedRef.current.has(dueKey)) {
        alarmedRef.current.add(dueKey); changed = true;
        notify(t.title, "Tu cita/tarea programada es ahora");
      }
    }
    if (changed) localStorage.setItem("notacora_alarmed", JSON.stringify([...alarmedRef.current].slice(-300)));
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/tasks/notifications");
      setData(data);
      fireAlarms([...(data.overdue || []), ...(data.upcoming || [])]);
    } catch { /* silencioso */ }
  }, [fireAlarms]);

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [user, load]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = () => { setOpen(false); navigate("/agenda"); };
  const fmt = (t) => (t.due_date ? format(parseISO(t.due_date.length === 10 ? t.due_date + "T00:00:00" : t.due_date), t.due_date.length > 10 ? "dd MMM HH:mm" : "dd MMM", { locale: es }) : "");

  const toggleOpen = () => {
    setOpen((o) => !o);
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggleOpen} className={`${ICON_BTN} relative`} title="Notificaciones y alarmas" data-testid="notification-bell">
        <Bell size={18} />
        {data.count > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-count">{data.count}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl z-[80] p-2" data-testid="notification-panel">
          <p className="font-heading font-medium text-sm px-2 py-1.5">Recordatorios</p>
          {data.count === 0 ? (
            <p className="font-body text-sm text-slate-400 px-2 py-4 text-center">Todo al día ✨ Sin tareas pendientes próximas.</p>
          ) : (
            <>
              {data.overdue.map((t) => (
                <button key={t.id} onClick={go} className="w-full text-left px-2 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 flex items-start gap-2">
                  <AlertCircle size={15} className="text-rose-500 mt-0.5 shrink-0" />
                  <span className="flex-1 min-w-0"><span className="block text-sm truncate">{t.title}</span><span className="text-[11px] text-rose-500 font-medium">Vencida · {fmt(t)}</span></span>
                </button>
              ))}
              {data.upcoming.map((t) => (
                <button key={t.id} onClick={go} className="w-full text-left px-2 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 flex items-start gap-2">
                  <Clock size={15} className="text-amber-500 mt-0.5 shrink-0" />
                  <span className="flex-1 min-w-0"><span className="block text-sm truncate">{t.title}</span><span className="text-[11px] text-amber-500 font-medium">Próxima · {fmt(t)}</span></span>
                </button>
              ))}
            </>
          )}
          <button onClick={go} className="w-full text-center text-xs text-indigo-500 hover:text-indigo-400 font-medium py-2 mt-1 border-t border-slate-100 dark:border-slate-800" data-testid="notification-open-agenda">Abrir Agenda</button>
        </div>
      )}
    </div>
  );
}
